import currencyFormatter from 'currency-formatter';
import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import { branch, compose, defaultProps, lifecycle, renderComponent, renderNothing, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { dateFormats, intervals, links } from '../../../../../constants';
import { isBusinessAdmin, notCanceled, getIndividualPlanIdByUserIndustry, getSmallTeamPlanIdByUserIndustry } from '../../../../../services';
import { businessActions, businessSelector } from '../../../../../state/modules/business';
import { userActions } from '../../../../../state/modules/user';
import { pluralise } from '../../../../../utils';
import { ConfirmationModal, NumberFormField } from '../../../../components';
import { boxed, followedBy, nested } from '../../../../components/higher-order';
import Loading from '../../../loading';

const branchPlan = (planId, higherOrder) => branch(
  fp.flow(
    fp.over([fp.get('planId'), fp.get(`${planId}PlanId`)]),
    fp.spread(fp.eq)
  ),
  higherOrder
);

const TeamMembersField = branchPlan(
  'individual',
  renderNothing
)(({
  numberOfUsers,
  onNumberOfUserChanges,
  minNumberOfUsers,
  teamMembers,
  activeInvitations,
  quantity
}) => [
    <h2 key='heading'>
      Team members on your plan
    </h2>,
    <NumberFormField
      key='input'
      id='user-count'
      formFieldProps={{
        formGroupProps: {
          className: 'clearfix'
        }
      }}
      label={null}
      value={numberOfUsers}
      required={true}
      onChange={onNumberOfUserChanges}
      min={minNumberOfUsers} />,
    <p key='message'>
      You've got {quantity} {pluralise('Caddie user license', quantity)} for your business, including {teamMembers.length} active {pluralise('user', teamMembers.length)} & {activeInvitations.length} pending {pluralise('invitation', activeInvitations.length)}.
    </p>,
    <p key='spacer'></p>
  ]);

const TooManyUsersMessage = branch(
  ({ tooManyUsers }) => !tooManyUsers,
  renderNothing
)(() => (
  <p>
    Woah, that's a lot of users! Let us give you a helping hand by setting up a
    &nbsp;
    <a href={links.thinkCaddie.contact} target='_blank'>
      Caddie enterprise account
    </a>.
  </p>
));

const TooFewUsersMessage = branch(
  ({ tooFewUsers }) => !tooFewUsers,
  renderNothing
)(({
  activeInvitations,
  teamMembers
}) => (
    <p>
      Too few licences to cover your current usage.
    </p>
  ));

const percentDiscountText = (price, discountedPrice) => {
  const percentOff = (1 - discountedPrice / price);
  return `${(percentOff * 100).toFixed(0)}% off`;
};

const Price = ({ price, discountedPrice }) => discountedPrice ? (
  <span>
    <del>{price}</del>
    {` ${discountedPrice}`}
  </span>
) : (
  <span>{price}</span>
);

export const Equation = branchPlan(
  'individual',
  renderNothing
)(({
  numberOfUsers,
  unitPrice,
}) => (
    <p className='equation'>
      <small>
        <span className='updateNumber'>{numberOfUsers}</span>
        {' x '}
        {unitPrice}
      </small>
    </p>
  ));

const CancelModalMessage = ({
  individualPlanId,
  planId,
  smallTeamPlanId,
  teamMembers
}) => [
    <p key={0}>
      Are you sure you want to cancel your plan?
    </p>,
    fp.get(planId)({
      [individualPlanId]: (
        <p key={1}>
          You will still be able to view your account and past CPD activity,
          however you will not be able to add and track your CPD.
        </p>
      ),
      [smallTeamPlanId]: (
        <p key={1}>
          Your {teamMembers.length} team {pluralise('member', teamMembers.length)} (including yourself)
          will still be able to view their account and past CPD activity,
          however they will not be able to add and track additional CPD.
        </p>
      )
    })
  ];

const CancelModal = compose(
  withProps(({
    individualPlanId,
    planId,
    planName,
    smallTeamPlanId,
    teamMembers
  }) => ({
    headingText: (
      <span>
        You're cancelling your {he.decode(planName)} plan
      </span>
    ),
    message: <CancelModalMessage {...{
      individualPlanId,
      planId,
      smallTeamPlanId,
      teamMembers
    }} />
  }))
)(({
  headingText,
  message,
  onCancel,
  onConfirm,
  onShow,
  show
}) => [
    <ConfirmationModal {...{
      key: 0,
      show,
      actions: [{
        onConfirmAction: onConfirm,
        actionLabel: "Yes, I'm sure",
      }],
      cancel: onCancel,
      message,
      headingText,
      cancelLabel: 'No, keep my plan active'
    }} />,
    <p key={1} className='text-center'>
      <Button
        bsStyle='plain'
        className='inline'
        onClick={onShow}>
        Cancel plan
      </Button>
    </p>
  ]);

const PlanDetails = ({
  activeInvitations,
  individualPlanId,
  numberOfUsers,
  onConfirm,
  onNumberOfUserChanges,
  plan: {
    interval: {
      interval
    },
    minQuantity: minNumberOfUsers,
    name: planName,
    unitPrice: {
      currencyCode,
      minorAmount
    }
  },
  planId,
  smallTeamPlanId,
  subscription: {
    endDate,
    quantity,
    id: hasSubscription,
    isPaused: subscriptionPaused
  },
  teamMembers,
  tooFewUsers,
  tooManyUsers,
  totalPrice,
  unitPrice,
  totalDiscountedPrice,
}) => [
    <div key='box-section' className='box-section'>
      <h2>
        {he.decode(planName)} plan
      </h2>
      <hr />
      <TeamMembersField
        {...{
          individualPlanId,
          minNumberOfUsers,
          numberOfUsers,
          onNumberOfUserChanges,
          planId,
          smallTeamPlanId,
          teamMembers,
          activeInvitations,
          quantity
        }} />
    </div>,
    <TooManyUsersMessage {...{
      key: 'too-many-users',
      tooManyUsers
    }} />,
    <TooFewUsersMessage {...{
      key: 'too-few-users',
      activeInvitations,
      teamMembers,
      tooFewUsers
    }} />,
    <h2 key='interval'>
      {fp.flow(fp.get(interval), fp.upperFirst)(intervals)} payment amount
    </h2>,
    <Equation
      {...{
        key: 'equation',
        individualPlanId,
        planId,
        numberOfUsers,
        smallTeamPlanId,
        unitPrice,
      }}
    />,
    totalDiscountedPrice ? (
      <p key='discount' className='equation'>
        <span className='updateNumber'>
          Discount: {percentDiscountText(totalPrice, totalDiscountedPrice)}
        </span>
      </p>
    ) : undefined,
    <p key='total' className='total'>
      <i>$</i>
      <span className='total'>
        <Price price={totalPrice} discountedPrice={totalDiscountedPrice}/>
      </span>
      &nbsp;
      <span className='note'>
        per {interval} direct debit, ex GST
        <br />
        payment from credit card
      </span>
    </p>,
    <h2 key='next-payment-heading'>
      Next payment date
    </h2>,
    <p key='next-payment-date' className='nextPaymentDate'>
      {dateFnsCompat.format(endDate, dateFormats.LongDate)}
    </p>
  ];

const InactiveMessage = () => [
  <p key={0} className='text-center'>
    Your Caddie subscription is inactive.
  </p>,
  <p key={1} className='text-center'>
    Please provide your payment details to activate your subscription.
  </p>
];

const findPlan = (planId) => fp.find(({ id }) => id === planId);

export default compose(
  defaultProps({
    onCancelPlan: fp.noop,
    onUpdatePlan: fp.noop
  }),
  connect(({
    business: {
      invitations,
      isFetching: isFetchingBusiness,
      subscription: businessSubscription,
      teamMembers,
      ...business
    },
    configuration: {
      stripeConfig: {
        plansInUse,
      }
    },
    payment: {
      plans,
      subscription,
    },
    user: {
      currentSubscription: userSubscription,
      currentUser: user,
      isFetching: isFetchingUser,
      profile: userProfile,
    }
  }) => ({
    business: businessSelector({ business }),
    businessSubscription,
    invitations,
    isFetchingBusiness,
    isFetchingUser,
    plansInUse,
    plans,
    teamMembers,
    user,
    userProfile,
    userSubscription,
    subscription,
  }), (dispatch) => ({
    cancelBusinessSubscription: bindActionCreators(businessActions.business.cancelSubscription, dispatch),
    cancelUserSubscription: bindActionCreators(userActions.user.cancelSubscription, dispatch),
  })),
  withProps(({
    isFetchingBusiness,
    isFetchingUser,
    user,
    userProfile
  }) => ({
    isLoading: isFetchingBusiness || isFetchingUser,
    isUserBusinessAdmin: isBusinessAdmin(user),
    userIndustryName: userProfile !== undefined ? userProfile.stat.industryName : "",
  })),
  withProps(({
    businessSubscription,
    isUserBusinessAdmin,
    userSubscription,
    plansInUse,
    userIndustryName,
  }) => ({
    subscription: isUserBusinessAdmin ?
      notCanceled(businessSubscription) :
      notCanceled(userSubscription),
    individualPlanId: getIndividualPlanIdByUserIndustry(plansInUse, userIndustryName),
    smallTeamPlanId: getSmallTeamPlanIdByUserIndustry(plansInUse, userIndustryName)
  })),
  withProps(({
    subscription
  }) => ({
    planId: subscription.planId,
  })),
  boxed,
  branch(
    fp.get('isLoading'),
    renderComponent(Loading)
  ),
  branch(
    ({
      subscription: {
        id: hasSubscription,
        paused
      }
    }) => !hasSubscription || paused,
    renderComponent(InactiveMessage)
  ),
  withProps(({
    cancelBusinessSubscription,
    cancelUserSubscription,
    individualPlanId,
    invitations,
    planId,
    plans,
    smallTeamPlanId
  }) => ({
    activeInvitations: fp.filter(({ status }) => status === 'Sent')(invitations),
    cancelSubscription: fp.get(planId)({
      [individualPlanId]: cancelUserSubscription,
      [smallTeamPlanId]: cancelBusinessSubscription
    }),
    plan: findPlan(planId)(plans)
  })),
  withStateHandlers(({
    subscription: {
      quantity: numberOfUsers
    }
  }) => ({
    numberOfUsers,
    show: false
  }), {
      onNumberOfUserChanges: () => ({
        value
      }) => ({
        numberOfUsers: parseInt(value, 10)
      }),
      onShow: () => () => ({ show: true }),
      onCancel: () => () => ({ show: false })
    }
  ),
  withProps(({
    activeInvitations,
    numberOfUsers,
    plan: {
      maxQuantity,
      unitPrice: {
        currencyCode: code,
        minorAmount
      }
    },
    teamMembers,
    upcomingInvoice,
  }) => ({
    tooFewUsers: numberOfUsers < teamMembers.length + activeInvitations.length,
    tooManyUsers: numberOfUsers > maxQuantity,
    totalPrice: currencyFormatter.format(minorAmount * numberOfUsers / 100, { format: '%v' }),
    unitPrice: currencyFormatter.format(minorAmount / 100, { code }),
    // Set discounted price to undefined if there is no discounts on subscription
    totalDiscountedPrice: upcomingInvoice && upcomingInvoice.totalDiscountAmounts.length > 0
      ? currencyFormatter.format(upcomingInvoice.total / 100, { format: '%v' }) 
      : undefined,
  })),
  withHandlers({
    onConfirm: ({
      business,
      cancelSubscription,
      individualPlanId,
      onCancel,
      onCancelPlan,
      planId,
      smallTeamPlanId
    }) => () => fp.over(
      () => fp.flow(
        fp.get(planId),
        fp.concat([]),
        fp.filter(fp.identity),
        fp.spread(cancelSubscription)
      )({
        [individualPlanId]: undefined,
        [smallTeamPlanId]: business ? business.id : undefined
      }),
      onCancel,
      onCancelPlan
    )()
  }),
  followedBy(0, ({
    individualPlanId,
    plan: {
      name: planName
    },
    planId,
    onCancel,
    onConfirm,
    onShow,
    show,
    smallTeamPlanId,
    teamMembers
  }) => [
      <CancelModal
        {...{
          key: 1,
          individualPlanId,
          planId,
          planName,
          onCancel,
          onConfirm,
          onShow,
          show,
          smallTeamPlanId,
          teamMembers
        }} />
    ]),
  nested([
    ({ children }) => (
      <div className='business-subscription clearfix'>
        {children}
      </div>
    )
  ]),
  branchPlan(
    'smallTeam',
    compose(
      connect((state) => ({
        isUpdating: state.business.isUpdating
      }), (dispatch) => ({
        updateSubscription: bindActionCreators(businessActions.business.updateSubscription, dispatch)
      })),
      lifecycle({
        componentWillReceiveProps({
          isUpdating,
          onUpdatePlan
        }) {
          const {
            isUpdating: wasUpdating
          } = this.props;

          if (wasUpdating && !isUpdating) {
            onUpdatePlan();
          }
        }
      }),
      withHandlers({
        onSubmit: ({
          business: {
            id: businessId
          },
          numberOfUsers,
          subscription,
          updateSubscription
        }) => (e) => {
          e.preventDefault();
          e.stopPropagation();

          updateSubscription({
            businessId,
            subscription: {
              ...subscription,
              quantity: numberOfUsers
            }
          });
        }
      }),
      nested([
        ({
          children,
          onSubmit
        }) => (
            <form
              noValidate
              onSubmit={onSubmit}>
              {children}
            </form>
          )
      ]),
      followedBy(0, ({
        isUpdating,
        tooFewUsers,
        tooManyUsers
      }) => [
          <p key={1} className='text-center'>
            <Button type='submit' disabled={tooFewUsers || tooManyUsers}>
              {
                isUpdating ?
                  <Spinner name='circle' className='spinner' fadeIn='none' /> :
                  'Update plan'
              }
            </Button>
          </p>
        ])
    )
  )
)(PlanDetails);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/components/plan-details.js